import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Assignment,
  Dashboard,
  DocumentScanner,
  FormatListNumbered,
  HowToVote,
  MedicalInformation,
  Flag,
  PregnantWoman,
  Notifications,
} from "@mui/icons-material";
import ChatIcon from "@mui/icons-material/Chat";
import TextsmsIcon from "@mui/icons-material/Textsms";
import { faPrescription } from "@fortawesome/free-solid-svg-icons";

import "../styles/patient-dashboard.css";

import Chat from "../components/Chat/Chat";
import MedicineList from "../components/Medicine/MedicineList";
import Medicine from "../components/Medicine/Medicine";
import DoctorReviewNotes from "../components/DoctorReviewNotes";
import ReportsList from "../components/ReportsList";
import ComplaintsList from "../components/ComplaintsList";
import Vitals from "../components/Vitals";
import { usePatientData } from "../hooks/usePatientData";
import { useVitalsData } from "../hooks/useVitalsData";
import { useWellnessData } from "../hooks/useWellnessData";
import { useRiskAssessment } from "../hooks/useRiskAssessment";
import { usePatientWellnessData } from "../hooks/usePatientWellnessData";
import { usePatientInfo } from "../hooks/usePatientInfo";
import {
  useHrvData,
  useWalkData,
  useSleepScoreData,
  useSleepDurationData,
  useCalories,
  useFineGrainedHeartRateData,
  useFineGrainedHRVData,
  useFineGrainedStressData,
  useFineGrainedBloodPressureData,
} from "../hooks/useChartData";
import { useWellnessDataFineGrained } from "../hooks/useWellnessDataFineGrained";
import { useActivityData } from "../hooks/useActivityData";
import Risks from "../components/Risks/Risks";
import ANC from "../components/ANC/ANC";
import Alerts from "../components/Alerts/Alerts";
import Cards from "../components/Cards/Cards";
import Header from "../components/Header/Header";
import ProfileCard from "../components/Profile/ProfileCard";
import SendSMS from "../components/SMS/SendSMS2";
import { usePatientWellnessDataForDemo } from "../hooks/usePatientWellnessDataForDemo";
import { useLastNEmergencyRiskFactor } from "../hooks/useEmergencyRiskFactors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import EnhancedPrescriptionForm from "../components/EnhancedPrescriptionForm";
import FileUploadComponent from "../components/Upload/FileUploadComponent";
import PreviousPrescription from "./PreviousPrescription";

const PatientDashboard = () => {
  let { id } = useParams();
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [activeDiv, setActiveDiv] = useState("appointments");

  const {
    data: patientData,
    isLoading: isPatientLoading,
    isError: isPatientError,
    error: patientError,
  } = usePatientData(id);
  const {
    data: vitalsData,
    isLoading: isVitalsLoading,
    isError: isVitalsError,
    error: vitalsError,
  } = useVitalsData(id);
  const {
    data: wellnessData,
    isLoading: isWellnessLoading,
    isError: isWellnessError,
    error: wellnessError,
  } = useWellnessData(id);
  const {
    data: wellnessDataFineGrained,
    isLoading: isWellnessDataFineGrainedLoading,
    isError: isWellnessDataFineGrainedError,
    error: wellnessDataFineGrainedError,
  } = useWellnessDataFineGrained(id);
  const {
    data: activityData,
    isLoading: isActivityLoading,
    isError: isActivityError,
    error: activityError,
  } = useActivityData(id);
  const {
    data: riskAssessmentData,
    isLoading: isRiskAssessmentLoading,
    isError: isRiskAssessmentError,
    error: riskAssessmentError,
  } = useRiskAssessment(id);
  const {
    data: emergencyRiskAssessmentData,
    isLoading: isEmergencyRiskAssessmentLoading,
    isError: isEmergencyRiskAssessmentError,
    error: emergencyRiskAssessmentError,
  } = useLastNEmergencyRiskFactor(id);
  // const {
  //   data: patientWellnessData,
  //   isLoading: isPatientWellnessLoading,
  //   isError: isPatientWellnessError,
  //   error: patientWellnessError,
  // } = usePatientWellnessData(id);
  const {
    data: patientWellnessData,
    isLoading: isPatientWellnessLoading,
    isError: isPatientWellnessError,
    error: patientWellnessError,
  } = usePatientWellnessDataForDemo(id);
  const {
    data: patientInfo,
    isLoading: isPatientInfoLoading,
    isError: isPatientInfoError,
    error: patientInfoError,
  } = usePatientInfo(id);

  // console.log(emergencyRiskAssessmentData);
  // console.log(riskAssessmentData);
  // console.log(patientWellnessData);
  // console.log(wellnessData);

  const hrvData = useHrvData(patientData);
  const sleepDurationData = useSleepDurationData(patientData);
  const sleepScoreData = useSleepScoreData(patientData);
  const walkData = useWalkData(patientData);
  const calorieData = useCalories(patientData);
  const fineGrainedHeartRateData = useFineGrainedHeartRateData(
    wellnessDataFineGrained
  );
  const fineGrainedHRVData = useFineGrainedHRVData(wellnessDataFineGrained);
  const fineGrainedStressData = useFineGrainedStressData(
    wellnessDataFineGrained
  );
  const fineGrainedBloodPressureData = useFineGrainedBloodPressureData(
    wellnessDataFineGrained
  );

  const showDiv = (divId) => {
    setActiveDiv(divId);
  };

  useEffect(() => {
    setSelectedMedicine(null);
  }, [activeDiv]);

  if (isPatientError) return <div>Error: {patientError.message}</div>;
  if (isVitalsError) return <div>Error: {vitalsError.message}</div>;
  if (isWellnessError) return <div>Error: {wellnessError.message}</div>;
  if (isWellnessDataFineGrainedError)
    return <div>Error: {wellnessDataFineGrainedError.message}</div>;
  if (isActivityError) return <div>Error: {activityError.message}</div>;
  if (isRiskAssessmentError)
    return <div>Error: {riskAssessmentError.message}</div>;
  if (isPatientWellnessError)
    return <div>Error: {patientWellnessError.message}</div>;
  if (isPatientInfoError) return <div>Error: {patientInfoError.message}</div>;

  return (
    <>
      <Header
        pageTitle={"Patient's Profile"}
        showBackButton={"true"}
        activeDiv={activeDiv}
        showDiv={showDiv}
      />
      {isPatientLoading ||
      isVitalsLoading ||
      isWellnessLoading ||
      isWellnessDataFineGrainedLoading ||
      isActivityLoading ||
      isRiskAssessmentLoading ||
      isPatientWellnessLoading ||
      isPatientInfoLoading ? (
        <div
          className="z-40 w-full flex justify-center items-center"
          style={{
            height: "calc(100vh - 210px)",
          }}
        >
          <div className="border-[6px] border-solid border-zinc-200 rounded-full border-t-[8px] border-t-[#3F4A9C] w-14 h-14 animate-spin"></div>
        </div>
      ) : (
        <div
          className="overflow-y-auto px-3 pb-3"
          style={{
            height: "calc(100vh - 96px)",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <div
            className="grid grid-cols-1 md:grid-cols-6 gap-3 "
            // style={{ minHeight: "calc(100vh - 125px)" }}
          >
            <div className="h-full">
              <ProfileCard patientInfo={patientInfo} showDiv={showDiv} />
            </div>

            <div className="col-span-1 md:col-span-5">
              <div className="bg-white rounded-3 p-3">
                {/* Content sections - unchanged */}
                {activeDiv === "appointments" && (
                  <Cards
                    riskAssessmentData={riskAssessmentData}
                    fineGrainedHeartRateData={fineGrainedHeartRateData}
                    wellnessData={wellnessData}
                    patientWellnessData={patientWellnessData}
                    fineGrainedHRVData={fineGrainedHRVData}
                    sleepScoreData={sleepScoreData}
                    sleepDurationData={sleepDurationData}
                    activityData={activityData}
                    walkData={walkData}
                    calorieData={calorieData}
                    fineGrainedStressData={fineGrainedStressData}
                    fineGrainedBloodPressureData={fineGrainedBloodPressureData}
                  />
                )}
                {activeDiv === "alerts" && (
                  <Alerts
                    riskAssessmentData={riskAssessmentData}
                    emergencyRiskAssessmentData={emergencyRiskAssessmentData}
                  />
                )}
                {activeDiv === "treatmentPlan" && <Chat patient_id={id} />}
                {activeDiv === "send-sms" && <SendSMS patient_id={id} />}
                {activeDiv === "document" &&
                  (selectedMedicine ? (
                    <Medicine
                      patient_id={id}
                      test_date={selectedMedicine.test_date}
                      test_id={selectedMedicine.test_id}
                      onBack={() => setSelectedMedicine(null)}
                    />
                  ) : (
                    <MedicineList
                      patient_id={id}
                      setSelectedMedicine={setSelectedMedicine}
                    />
                  ))}
                {activeDiv === "task" && <DoctorReviewNotes patient_id={id} />}
                {activeDiv === "medical" && <ReportsList patient_id={id} />}
                {activeDiv === "assessment" && (
                  <ComplaintsList patient_id={id} />
                )}
                {activeDiv === "vitals" && <Vitals patient_id={id} />}
                {activeDiv === "risk_factors" && <Risks patient_id={id} />}
                {activeDiv === "ANCs" && <ANC patient_id={id} />}
                {activeDiv === "prescription" && (
                  <EnhancedPrescriptionForm patientPhone={patientInfo.phone} />
                )}
                {activeDiv === "previous-prescriptions" && (
                  <PreviousPrescription patient_id={id} />
                )}
                {activeDiv === "upload" && (
                  <FileUploadComponent patient_id={id} />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PatientDashboard;
